import styled, { css } from "styled-components";

import { DESKTOP_BREAKPOINT } from "../AppShell/styles";
import { buttonLoadingCss } from "../Button";

const MOBILE = `not all and (min-width: ${DESKTOP_BREAKPOINT})`;

export type ModalSize = "sm" | "md" | "lg";

const WIDTHS: Record<ModalSize, string> = { sm: "400px", md: "480px", lg: "880px" };

type BackgroundProps = {
  /** The modal (not the loader) becomes a sheet from the bottom on phones */
  $sheet?: boolean;
};

/* The dark backdrop: the content centered 16px from the edges */
export const Background = styled.div<BackgroundProps>`
  position: fixed;
  inset: 0;
  z-index: 9999;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  font-family: Inter, sans-serif;
  background: rgba(0, 0, 0, 0.7);

  ${props =>
    props.$sheet &&
    css`
      @media ${MOBILE} {
        align-items: flex-end;
        padding: 0;
      }
    `}
`;

type ContainerProps = {
  $size: ModalSize;
  $plus: boolean;
};

/* Same as the Domains "connect a Keepo" modal: a bordered frame, header, body that scrolls and a bar at the bottom */
export const Container = styled.div<ContainerProps>`
  display: flex;
  flex-direction: column;
  width: 100%;
  max-width: ${props => WIDTHS[props.$size]};
  max-height: min(${props => (props.$size === "lg" ? "85dvh" : "640px")}, calc(100dvh - 2rem));
  overflow: hidden;
  color: var(--color-neutral-white);
  text-align: left;
  background: var(--color-neutral-dark);
  border: 1px solid var(--color-neutral-base);
  border-radius: 1.25rem;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.5);
  outline: none;

  /* A Keepo Plus offer: the sidebar banner's green glow */
  ${props =>
    props.$plus &&
    css`
      background:
        radial-gradient(120% 140% at 0% 0%, rgba(87, 242, 139, 0.16) 0%, rgba(87, 242, 139, 0) 60%),
        var(--color-neutral-dark);
      border-color: rgba(87, 242, 139, 0.28);
    `}

  @media ${MOBILE} {
    max-width: none;
    max-height: 85dvh;
    border-bottom: 0;
    border-radius: 1.25rem 1.25rem 0 0;
  }
`;

type HeaderProps = {
  $divided: boolean;
};

export const Header = styled.header<HeaderProps>`
  display: flex;
  flex-shrink: 0;
  gap: 1rem;
  align-items: flex-start;
  justify-content: space-between;
  padding: 1.25rem 1.25rem 1rem;

  /* A body that scrolls under it (e.g. a grid) */
  ${props =>
    props.$divided &&
    css`
      border-bottom: 1px solid var(--color-neutral-base);
    `}
`;

export const Heading = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  min-width: 0;
`;

export const Title = styled.h2`
  font-family: Inter, sans-serif;
  font-size: 1.0625rem;
  font-weight: 800;
  line-height: 1.5rem;
  color: var(--color-neutral-white);
`;

export const Subtitle = styled.p`
  margin-top: 0.125rem;
  font-size: 0.8125rem;
  line-height: 1.125rem;
  color: var(--color-neutral-150);
`;

/* Above the title (e.g. the Keepo Plus tag) */
export const Eyebrow = styled.div`
  margin-bottom: 0.5rem;
`;

export const CloseButton = styled.button`
  display: flex;
  flex-shrink: 0;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin-left: auto;
  color: var(--color-neutral-white);
  background: var(--color-neutral-base);
  border-radius: 100%;
  transition: background-color 0.15s;

  &:hover:not(:disabled) {
    background: var(--color-neutral-light);
  }

  &:disabled {
    cursor: not-allowed;
    opacity: 0.5;
  }
`;

type BodyProps = {
  $flush: boolean;
  $divided: boolean;
};

export const Body = styled.div<BodyProps>`
  flex: 1;
  min-height: 0;
  padding: ${props => (props.$flush ? "0" : `${props.$divided ? "1.25rem" : "0"} 1.25rem 1.25rem`)};
  overflow-y: auto;
  font-size: 0.875rem;
  line-height: 1.25rem;
  color: #d6d8e0;

  &:empty {
    display: none;
  }
`;

/* Above the actions, always in sight (under a long body it would go unseen) */
export const ErrorBand = styled.p`
  flex-shrink: 0;
  padding: 0.75rem 1.25rem;
  font-size: 0.8125rem;
  line-height: 1.125rem;
  color: #ffb066;
  background: rgba(255, 122, 0, 0.08);
  border-top: 1px solid var(--color-neutral-base);
`;

type FooterProps = {
  $plus: boolean;
};

/* The actions: a secondary in gray text, then the main one as a pill */
export const Footer = styled.footer<FooterProps>`
  display: flex;
  flex-shrink: 0;
  gap: 0.75rem;
  align-items: center;
  justify-content: flex-end;
  padding: 1rem 1.25rem;
  background: ${props => (props.$plus ? "rgba(0, 0, 0, 0.25)" : "var(--color-neutral-darkness)")};
  border-top: 1px solid var(--color-neutral-base);

  @media ${MOBILE} {
    padding-bottom: calc(1rem + env(safe-area-inset-bottom));
  }
`;

/* The footer's secondary action (Cancel, Not now, Skip) */
export const SecondaryButton = styled.button`
  padding: 0.5rem 0.75rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--color-neutral-150);
  transition: color 0.15s;

  &:hover:not(:disabled) {
    color: var(--color-neutral-white);
  }

  &:disabled {
    cursor: not-allowed;
  }
`;

export type ModalButtonVariant = "INFO" | "DANGER" | "WHITE" | "plus";

const VARIANTS: Record<ModalButtonVariant, ReturnType<typeof css>> = {
  INFO: css`
    color: var(--color-neutral-white);
    background-color: var(--color-brand-blue);
  `,
  DANGER: css`
    color: var(--color-neutral-white);
    background-color: var(--color-feedback-danger);
  `,
  WHITE: css`
    color: var(--color-neutral-darkness);
    background-color: var(--color-neutral-white);
  `,
  plus: css`
    font-weight: 800;
    color: var(--color-neutral-darkness);
    background-color: var(--color-brand-plus);
  `,
};

type PrimaryButtonProps = {
  $variant: ModalButtonVariant;
  $loading?: boolean;
};

/* The footer's main action, as a pill (a real button: Tab reaches it, Enter and Space press it). Half the bar on
   phones */
export const PrimaryButton = styled.button<PrimaryButtonProps>`
  min-width: 8.5rem;
  padding: 0.5rem 1rem;
  font-family: Inter, sans-serif;
  font-size: 0.875rem;
  font-weight: 700;
  line-height: 1rem;
  border-radius: 100px;
  transition: filter 0.15s;
  ${props => VARIANTS[props.$variant]}

  &:hover:not([aria-disabled="true"]) {
    filter: brightness(1.08);
  }

  &:focus-visible {
    outline: 2px solid var(--color-neutral-white);
    outline-offset: 2px;
  }

  /* Nothing to do yet (e.g. nothing new picked): still focusable, so it can say why. Saving shows the loader */
  &[aria-disabled="true"]:not([aria-busy="true"]) {
    color: var(--color-neutral-150);
    cursor: not-allowed;
    background-color: var(--color-neutral-base);
  }

  ${props => props.$loading && buttonLoadingCss}

  @media ${MOBILE} {
    flex: 1;
  }
`;
